import { useQuery } from "@tanstack/react-query";
import { fetchHandler } from "@/lib/fetch-handler";
import Price from "@/components/price";
import type { CartItem, ShippingArrayDataType } from "@/lib/bagisto/types";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ReactNode } from "react";

interface OrderSummaryProps {
    items: CartItem[];
    subTotal: string;
    currencyCode: string;
    cartId?: string;
    itemsQty?: string | number;
    children: ReactNode;
}

export default function OrderSummary({
    items,
    subTotal,
    currencyCode,
    cartId,
    itemsQty,
    children
}: OrderSummaryProps) {
    // Fetch shipping methods
    const { data: shippingData, isLoading: isLoadingShipping } = useQuery({
        queryKey: ["shipping-methods", cartId, itemsQty],
        enabled: !!cartId,
        queryFn: async () =>
            fetchHandler({
                url: "/checkout/shippingMethods",
                method: "GET",
            }),
    });

    const shippingMethods = shippingData?.shippingMethods?.shippingMethods;

    return (
        <div className="flex flex-col pt-[60px] gap-5 w-full">

            {/* Title */}
            <h2 className="font-oswald font-medium text-[20px] leading-[100%] tracking-[-0.02em] uppercase text-neutral-500">
                Order Summary
            </h2>

            {/* Summary Box */}
            <div className="w-full rounded-[20px] bg-background-50 px-5 py-[40px] flex flex-col gap-[30px]">

                {/* Items Count */}
                <span className="font-oswald font-medium text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                    {items.length} Item{items.length !== 1 ? "s" : ""}
                </span>

                {/* Per-item breakdown */}
                <div className="flex flex-col gap-5">
                    {items.map((item: CartItem) => (
                        <div key={item.id} className="flex items-center justify-between">
                            <span className="font-roboto font-medium text-[16px] leading-[25px] tracking-[0%] text-neutral-300">
                                {item.quantity}X {item.product.name}
                            </span>
                            <Price
                                amount={item.total}
                                currencyCode={currencyCode}
                                className="font-oswald font-medium text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500"
                            />
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <div className="w-full h-0 border-t border-stroke-light" />

                {/* Dynamic Delivery Charges */}
                {isLoadingShipping ? (
                    <div className="flex justify-between">
                        <Skeleton width={96} height={20} />
                        <Skeleton width={64} height={20} />
                    </div>
                ) : shippingMethods && shippingMethods.length > 0 ? (
                    shippingMethods.map((carrier: ShippingArrayDataType) => {
                        const shippingOption = carrier?.methods;
                        if (!shippingOption) return null;

                        return (
                            <div key={shippingOption.code} className="flex items-center justify-between">
                                <span className="font-roboto font-medium text-[16px] leading-[25px] tracking-[0%] text-neutral-300">
                                    {shippingOption?.label}
                                </span>
                                <span className="font-oswald font-medium text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                                    {shippingOption.formattedBasePrice || "FREE DELIVERY"}
                                </span>
                            </div>
                        );
                    })
                ) : (
                    <div className="flex items-center justify-between">
                        <span className="font-roboto font-medium text-[16px] leading-[25px] tracking-[0%] text-neutral-300">
                            Delivery Charges
                        </span>
                        <span className="font-oswald font-medium text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                            FREE DELIVERY
                        </span>
                    </div>
                )}

                {/* Divider */}
                <div className="w-full h-0 border-t border-stroke-light" />

                {/* Total Amount */}
                <div className="flex items-center justify-between">
                    <span className="font-oswald font-medium text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500">
                        Total Amount
                    </span>
                    <Price
                        amount={subTotal}
                        currencyCode={currencyCode}
                        className="font-oswald font-medium text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500"
                    />
                </div>

                {/* Custom Button */}
                {children}

            </div>
        </div>
    );
}
